import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { PositionSizeCalculator } from "@/components/tools/PositionSizeCalculator";
import { PropFirmGuides } from "@/components/tools/PropFirmGuides";

const SITE = "https://xauproacademy.com";
const PATH = "/position-size-calculator";
const TITLE = "Gold Lot Size Calculator for XAUUSD | XAU Pro Academy";
const DESC =
  "Free gold lot size and XAUUSD pip calculator. Enter balance, risk and stop loss to get exact lots, pip value, margin and prop firm daily drawdown room. Includes a gold pip value cheatsheet.";

const FAQ = [
  {
    q: "How do I calculate lot size for gold (XAUUSD)?",
    a: "One standard lot of gold is 100 ounces, so every $1 move in price is worth $100 per lot. Divide the amount you are willing to lose by (stop distance in dollars x 100). Example: risking $500 with a $20 stop gives 500 / (20 x 100) = 0.25 lots.",
  },
  {
    q: "What is a pip in gold?",
    a: "Brokers quote gold differently, which is why pip based gold calculators often disagree. This calculator avoids the confusion by using the stop distance in dollars: a move from 2,400.00 to 2,380.00 is a $20 stop, whatever your broker calls a pip.",
  },
  {
    q: "How much should I risk per trade?",
    a: "Professional desks typically risk between 0.25% and 1% of the account per position. On a funded or evaluation account, risk should also be measured against the remaining daily loss allowance, not only the balance.",
  },
  {
    q: "How does the prop firm drawdown check work?",
    a: "Enter your daily loss limit and the loss already realised today. The calculator shows how much daily room remains and what share of it the planned trade would consume. Above 30% of the remaining room, one stop out materially threatens the account.",
  },
  {
    q: "Why is a very tight stop on gold risky?",
    a: "Gold routinely moves several dollars within minutes around London and New York opens. Stops below a few dollars sit inside normal noise and are frequently hit without the trade idea being invalidated. Structure, not a desired lot size, should define the stop.",
  },
  {
    q: "What lot size should I use on a $100,000 prop firm account for gold?",
    a: "At 0.5% risk ($500) with a $20 stop, the size is 500 / (20 x 100) = 0.25 lots. One stop out then uses 10% of a typical 5% ($5,000) daily loss limit.",
  },
  {
    q: "How much is a $1 move in gold worth?",
    a: "$1 per 0.01 lot, $10 per 0.10 lot and $100 per 1.00 standard lot, because one standard lot is 100 ounces.",
  },
  {
    q: "Why do brokers show different pip sizes for gold?",
    a: "Some brokers define a gold pip as $0.01, others as $0.10 or $1.00. The dollar value of the move is identical; only the label changes. Sizing by stop distance in dollars removes the ambiguity.",
  },
];

export const Route = createFileRoute("/position-size-calculator")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "gold lot size calculator, xauusd pip calculator, position size calculator, xauusd lot size calculator, gold pip value, prop firm lot calculator, prop firm drawdown calculator, lot size calculator",
      },
      { property: "og:title", content: "Gold Lot Size Calculator: XAUUSD Pip and Position Size" },
      {
        property: "og:description",
        content: "Exact lot size, pip value, margin and prop firm daily drawdown room in seconds. Free, no sign up.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Position Size Calculator (Gold and Forex)",
          url: `${SITE}${PATH}`,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC, featureList: ["Lot size from risk percent", "Pip value", "Margin estimate", "Prop firm daily drawdown room"], browserRequirements: "Requires JavaScript. Modern HTML5 browser", softwareVersion: "2026.1", isAccessibleForFree: true, inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Position Size Calculator", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: PositionSizePage,
});

function PositionSizePage() {
  return (
    <Layout>
      <section className="container mx-auto max-w-7xl px-4 pt-14 md:pt-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Free risk tool</div>
        <h1 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-tight md:text-5xl">
          Gold Lot Size and XAUUSD Pip Calculator
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground md:text-lg">
          Enter your balance, risk and stop loss. Get the exact lot size, pip value and margin, plus how much of
          your prop firm daily loss limit the trade would use.
        </p>
      </section>

      <section className="container mx-auto mt-10 max-w-7xl px-4">
        <PositionSizeCalculator />
      </section>

      {/* How it works */}
      <section className="container mx-auto mt-16 max-w-4xl px-4">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">How to calculate position size</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>Position size answers one question: how many lots can you trade so that hitting your stop costs exactly the amount you planned to lose?</p>
          <div className="rounded-xl border border-border/60 bg-card/60 p-5 font-mono text-sm text-foreground">
            Lot size = Risk amount / (Stop distance x Value per unit per lot)
          </div>
          <ul className="list-disc space-y-2 pl-5">
            <li><span className="text-foreground">Gold (XAUUSD):</span> 1 lot = 100 oz, so a $1 move = $100 per lot. Risking $500 with a $20 stop: 500 / (20 x 100) = 0.25 lots.</li>
            <li><span className="text-foreground">EURUSD, GBPUSD:</span> 1 pip = $10 per standard lot. Risking $500 with a 25 pip stop: 500 / (25 x 10) = 2.00 lots.</li>
            <li><span className="text-foreground">USDJPY:</span> pip value depends on price: (0.01 x 100,000) / price per lot.</li>
          </ul>
          <p>The calculator rounds down to the nearest 0.01 lot, so your real risk never exceeds the plan.</p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">XAUUSD pip value and lot size cheatsheet</h2>
        <p className="mt-4 text-muted-foreground">Value of a gold price move per lot size. One standard lot equals 100 ounces.</p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border/60 bg-card/60">
          <table className="w-full text-left text-sm">
            <thead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr className="border-b border-border/60">
                <th className="p-3">Lot size</th><th className="p-3">Ounces</th><th className="p-3">$0.01 move</th><th className="p-3">$0.10 move</th><th className="p-3">$1.00 move</th><th className="p-3">$20 stop</th>
              </tr>
            </thead>
            <tbody className="font-mono text-foreground">
              {[
                ["0.01 (micro)", 1],
                ["0.10 (mini)", 10],
                ["0.50", 50],
                ["1.00 (standard)", 100],
              ].map(([l, oz]) => (
                <tr key={l as string} className="border-b border-border/40 last:border-0">
                  <td className="p-3">{l}</td>
                  <td className="p-3">{oz}</td>
                  <td className="p-3">${((oz as number) * 0.01).toFixed(2)}</td>
                  <td className="p-3">${((oz as number) * 0.1).toFixed(2)}</td>
                  <td className="p-3">${(oz as number).toFixed(2)}</td>
                  <td className="p-3">${((oz as number) * 20).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Prop firm lot size reference for gold</h2>
        <p className="mt-4 text-muted-foreground">Lot size at 0.5% risk with a $20 stop, and share of a 5% daily loss limit consumed by one stop out.</p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border/60 bg-card/60">
          <table className="w-full text-left text-sm">
            <thead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr className="border-b border-border/60">
                <th className="p-3">Account</th><th className="p-3">Risk (0.5%)</th><th className="p-3">Lot size</th><th className="p-3">Daily limit (5%)</th><th className="p-3">Room used</th>
              </tr>
            </thead>
            <tbody className="font-mono text-foreground">
              {[25_000, 50_000, 100_000, 200_000].map((a) => (
                <tr key={a} className="border-b border-border/40 last:border-0">
                  <td className="p-3">${a.toLocaleString("en-US")}</td>
                  <td className="p-3">${(a * 0.005).toLocaleString("en-US")}</td>
                  <td className="p-3">{(Math.floor(((a * 0.005) / 2000) * 100) / 100).toFixed(2)}</td>
                  <td className="p-3">${(a * 0.05).toLocaleString("en-US")}</td>
                  <td className="p-3">10%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Position sizing on prop firm accounts</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>Most funded accounts fail on the daily loss rule, not on a bad idea. Fixed lot sizes ignore the fact that the daily allowance shrinks after every loss.</p>
          <p>Size each position against the room left today. Keeping a single trade under roughly 30% of that room means two consecutive stops cannot end the account in one session.</p>
          <p>
            Read more in our <Link to="/prop-firm-gold" className="text-gold hover:underline">prop firm gold trading guide</Link>.
          </p>
        </div>
      </section>

      <PropFirmGuides />

      {/* CTA */}
      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="rounded-2xl border border-gold/30 bg-card/70 p-6 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Size is half the decision</div>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold md:text-3xl">
            The calculator sizes the trade. The desk qualifies it.
          </h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Every setup on the Signals Desk ships with a structural stop and fixed 1R risk, ready to plug into this calculator.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="gold">
              <Link to="/signals">See the Signals Desk <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/xauusd-analysis-today">XAUUSD analysis today</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/gold-trading-sessions">Gold trading hours clock</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto mt-16 max-w-4xl px-4 pb-6">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">Frequently asked questions</h2>
        <div className="mt-6 divide-y divide-border/60 rounded-xl border border-border/60 bg-card/60">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="cursor-pointer list-none font-medium text-foreground">{f.q}</summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <MacroToolsStrip />
      <div className="pb-16" />
    </Layout>
  );
}
